import type { UseFormRegisterReturn } from 'react-hook-form';
import { cn } from '@/utils/cn';

type CheckBoxProps = {
  children: React.ReactNode;
  register: UseFormRegisterReturn;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  description?: string;
  defaultChecked?: boolean;
  checked?: boolean;
  size?: 'md' | 'lg';
};

const CheckBox = ({
  children,
  register,
  onChange,
  description,
  defaultChecked = false,
  checked,
  size = 'md',
}: CheckBoxProps) => {
  const disabledStyle = 'disabled:cursor-not-allowed disabled:bg-kl-smoke disabled:text-kl-gray';
  const enabledStyle =
    'text-kl-black checked:!bg-white checked:!border-kl-gray hover:!border-kl-gray cursor-pointer';

  const _onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    register.onChange(event);
    if (onChange) onChange(event);
  };

  const sizeClasses = size === 'lg' ? 'size-8' : 'size-5';
  const wrapperMinHeight = size === 'lg' ? 'min-h-8' : 'min-h-6';
  // 同一ページに複数チェックボックスが並んでも ID が衝突しないよう name から一意に導出する。
  const descriptionId = `${register.name}-description`;

  return (
    <div className='relative flex items-center'>
      <div className={cn('flex items-center', wrapperMinHeight)}>
        <input
          id={register.name}
          {...(description ? { 'aria-describedby': descriptionId } : {})}
          name={register.name}
          type='checkbox'
          className={`${
            register.disabled ? disabledStyle : enabledStyle
          } rounded border-[0.05rem] border-kl-gray focus:ring-transparent ${sizeClasses}`}
          disabled={register.disabled}
          onBlur={register.onBlur}
          onChange={_onChange}
          ref={register.ref}
          {...(checked === undefined ? { defaultChecked } : { checked })}
        />
      </div>
      <div className='ml-2 text-sm leading-6'>
        <label
          htmlFor={register.name}
          className={cn(
            'font-medium',
            'select-none',
            '[&_a]:underline',
            register.disabled ? 'text-gray-500' : 'cursor-pointer text-gray-900',
          )}
        >
          {children}
        </label>
        {description && (
          <p id={descriptionId} className='text-gray-500'>
            {description}
          </p>
        )}
      </div>
    </div>
  );
};

export default CheckBox;
